/**
 * 隣接する 2 つのステップ確定位置を alpha（0〜1 にクランプ）で線形補間する。
 * 描画用アバター位置（motionState.renderPosition）の区分線形補間の 1 区間。
 * 物理は固定ステップ、描画は別の rAF で進むため、確定位置をそのまま描くと
 * 周期・位相のずれで歩行がガタつく（時間エイリアシング）。呼び出し側
 * （interpolatedRenderPosition）が直近 3 サンプルから区間を選ぶ。
 */
export const interpolateStepPositions = (
  prev: { x: number; y: number; z: number },
  curr: { x: number; y: number; z: number },
  alpha: number,
): { x: number; y: number; z: number } => {
  const t = Math.min(1, Math.max(0, alpha))
  return {
    x: prev.x + (curr.x - prev.x) * t,
    y: prev.y + (curr.y - prev.y) * t,
    z: prev.z + (curr.z - prev.z) * t,
  }
}

/**
 * XZ 方向をカメラ方位 yaw で回転する（カメラ相対移動）。yaw=0 で恒等（W=-Z のまま）。
 * 視点(follow カメラ)の向きを基準に WASD/矢印を解釈し、視点方向へ進めるために使う。
 */
export const rotateXZByYaw = (
  vector: { x: number; z: number },
  yaw: number,
): { x: number; z: number } => {
  const c = Math.cos(yaw)
  const s = Math.sin(yaw)
  return {
    x: vector.x * c + vector.z * s,
    z: -vector.x * s + vector.z * c,
  }
}

/** XZ ベクトルを正規化する。長さ 0 のときは {0,0} を返す。 */
export const normalizeXZ = (vector: {
  x: number
  z: number
}): { x: number; z: number } => {
  const length = Math.hypot(vector.x, vector.z)
  if (length === 0) {
    return { x: 0, z: 0 }
  }
  return { x: vector.x / length, z: vector.z / length }
}

/**
 * 最終姿勢 = Y ヨー(rotationY) ∘ 基準向き(base) を rapier の Rotation
 * （クォータニオン {x,y,z,w}）として返す。
 *
 * レンダラは同じ合成で箱を回転させる（threeRenderer の composeOrientation）。
 * 物理側がコライダーを回転させないと、90° 回した板（黄色い「Fall」床は
 * rotationY=π/2）や傾いたランプの当たり判定が描画と食い違うため、ここで揃える。
 * Qy(yaw) ⊗ base のハミルトン積を直接展開している（base.x..w）。
 */
export const composeYaw = (
  base: { x: number; y: number; z: number; w: number },
  yaw: number,
): { x: number; y: number; z: number; w: number } => {
  const half = (Number.isFinite(yaw) ? yaw : 0) / 2
  const s = Math.sin(half)
  const c = Math.cos(half)
  return {
    x: c * base.x + s * base.z,
    y: c * base.y + s * base.w,
    z: c * base.z - s * base.x,
    w: c * base.w - s * base.y,
  }
}

/**
 * 浮動小数の許容誤差。rapier（WASM の f32）と World（f64）の往復で生じる
 * 丸め差を「移動」と誤検知しないための余裕。シーン座標（studs）に対して
 * 十分小さく、ブロック 1 歩（1 stud）の移動は確実に検知できる。
 */
const POSE_EPSILON = 1e-4

export const vec3Equals = (
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
): boolean =>
  Math.abs(a.x - b.x) <= POSE_EPSILON &&
  Math.abs(a.y - b.y) <= POSE_EPSILON &&
  Math.abs(a.z - b.z) <= POSE_EPSILON

export const quatEquals = (
  a: { x: number; y: number; z: number; w: number },
  b: { x: number; y: number; z: number; w: number },
): boolean =>
  Math.abs(a.x - b.x) <= POSE_EPSILON &&
  Math.abs(a.y - b.y) <= POSE_EPSILON &&
  Math.abs(a.z - b.z) <= POSE_EPSILON &&
  Math.abs(a.w - b.w) <= POSE_EPSILON

/** 角度差を (-π, π] の最短角へ正規化する（±π 境界のラップアラウンド対策）。 */
export const normalizeAngleDelta = (delta: number): number =>
  Math.atan2(Math.sin(delta), Math.cos(delta))

/**
 * クォータニオンから Y 軸ヨー成分（ラジアン）を取り出す。
 * スタート地点の現在の向きからスポーン時のアバターの向きを導くために使う。
 * ロール・ピッチが混在する姿勢では水平面への射影のヨー
 * （回転行列の atan2(m13, m33)。platformCarry の platformYawOf が使う
 * quatToRobloxOrientation(...).y と同じ定義）になり、台追従の回転量と
 * 「合計ヨー」の解釈が一致する。
 */
export const yawFromQuat = (q: {
  x: number
  y: number
  z: number
  w: number
}): number =>
  Math.atan2(2 * (q.w * q.y + q.x * q.z), 1 - 2 * (q.x * q.x + q.y * q.y))
